import axios, { type AxiosInstance, AxiosError } from "axios";
import { API_ENDPOINTS } from "@/utils/constants";
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from "@/utils/tokens";

// 🔑 Max times we retry after refreshing token
const MAX_TOKEN_REFRESH_RETRIES = 1;

// Axios instance for public routes
export const publicApi: AxiosInstance = axios.create({
  baseURL: API_ENDPOINTS.base,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Axios instance for authenticated routes
export const cexApi: AxiosInstance = axios.create({
  baseURL: API_ENDPOINTS.cexapi,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Axios instance for authenticated routes
export const authApi: AxiosInstance = axios.create({
  baseURL: API_ENDPOINTS.base,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Attach JWT token to requests
cexApi.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for cexApi - handle 429 and other errors
cexApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Handle 429 Too Many Requests
    if (error.response?.status === 429) {
      console.warn('CEX API: Rate limited (429) - Too Many Requests');
      return Promise.reject(error);
    }
    console.error('CEX API Error:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);

// Attach JWT token to requests
authApi.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh queue handling
let isRefreshing = false;
let failedQueue: Array<{ resolve: (val: any) => void; reject: (err?: any) => void; }> = [];

const processFailedQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) prom.resolve(token);
    else prom.reject(error);
  });
  failedQueue = [];
};

// Response interceptor
authApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    // Handle 429 Too Many Requests - don't attempt refresh, just reject
    if (error.response?.status === 429) {
      console.warn('Rate limited (429) - Too Many Requests');
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest) {
      // Track retry count on the request itself
      originalRequest._retryCount = originalRequest._retryCount || 0;

      if (originalRequest._retryCount >= MAX_TOKEN_REFRESH_RETRIES) {
        // ❌ Already retried -> logout
        // clearTokens();
        // window.location.href = "/login";
        return Promise.reject(error);
      }

      originalRequest._retryCount += 1;

      console.log({ originalRequest, processFailedQueue });

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        // clearTokens();
        // window.location.href = "/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            const token = getAccessToken();
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return authApi(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        // 🔄 Refresh token
        const response = await publicApi.get("/v0/auth/refresh-token", {
          headers: { Authorization: `Bearer ${refreshToken}` },
        });

        const { accessToken } = response.data;
        const currentRefreshToken = getRefreshToken();
        const rememberMe = !!localStorage.getItem("refreshToken");
        setTokens(accessToken, currentRefreshToken, rememberMe);

        processFailedQueue(null, accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return authApi(originalRequest);
      } catch (refreshError) {
        processFailedQueue(refreshError, null);
        // clearTokens();
        // window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Public API error handling
publicApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error);
    return Promise.reject(error);
  }
);
